﻿import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumbs } from 'components/navigation';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { useDebounce } from 'hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { CheckService } from 'services/CheckService';
import { DateToLocal } from "components/dates";
import { NotificationToaster, AlertConfirm, NotificationInline } from 'components/notifications';
import { SetFiltering, SetRouting } from 'state/actions';
import { Intent } from '@blueprintjs/core';
import { ListingTable } from "components/listing";
import { ShowHide } from "components/layout";
import { FormTextInput } from "components/form-fields";
import { Tooltip } from 'components/tooltip';
import { LinkButton } from 'components/buttons'
import { Filter, FilterButton } from 'components/filtering';
import { TemplateStatuses, TemplateTypes } from "pages/checks/constants";
import { useTemplateConfig } from "pages/checks/hooks/useTemplateConfig";
import { StringToEllipsesString } from 'components/formatting';

export function ChecksTemplateListing(props) {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const dispatch = useDispatch();
    const { useGlobalEndpoint, requiredAction, title, baseRoute, isGlobal, canEditGlobal } = useTemplateConfig(props.useGlobalConfig);

    const canAccessPage = UserHasAccess(requiredAction);

    const [filterSetupComplete, setFilterSetupComplete] = useState(false);
    const [initalSetupComplete, setInitialSetupComplete] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState(`No ${isGlobal ? 'standard ' : ''}templates found, please try a different search and filter.`);
    const [loadingData, setLoadingData] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [tableSortBy, setTableSortBy] = useState({ headerName: "Created", sortName: "CREATEDATE" });
    const [tableSortDir, setTableSortDir] = useState("D");
    const [showFilters, setShowFilters] = useState(false);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [pageNumber, setPageNumber] = useState(1);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [templateOptions, setTemplateOptions] = useState(null);
    var navigate = useNavigate();

    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentSearchTerm = useRef();
    const inputRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [showArchiveTemplateConfirm, setShowArchiveTemplateConfirm] = useState(false);
    const [showCloneTemplateConfirm, setShowCloneTemplateConfirm] = useState(false);

    const userIsSuperInternal = UserHasAccess("Internal:Super");

    const sortableHeaders = [
        { headerName: "Name", sortName: "TEMPLATENAME" },
        { headerName: "Created", sortName: "CREATEDATE" },
        { headerName: "Published", sortName: "PUBLISHEDDATE" },
        { headerName: "Archived", sortName: "ARCHIVEDDATE" },
        { headerName: "Category", sortName: "CATEGORY.CATEGORYNAME" },
        { headerName: "Subcategory", sortName: "SUBCATEGORY.SUBCATEGORYNAME" },
        { headerName: "Type", sortName: "TEMPLATETYPE.TEMPLATETYPENAME" },
        { headerName: "Status", sortName: "TEMPLATESTATUS.TEMPLATESTATUSNAME" },
        { headerName: "Version", sortName: "VERSIONNUMBER" },
        { headerName: "Checks for this version", sortName: "NUMBEROFCHECKSFORCURRENTVERSION" },
        { headerName: "Checks for all versions", sortName: "TOTALNUMBEROFCHECKSFORALLVERSIONS" },
    ];

    const tableHeaders = ["Name", "Created", "Published", "Archived", "Category", "Subcategory", "Type", "Status", "Version", "Checks for this version", "Checks for all versions", "duplicate", "archive"];

    const filterName = 'checksTemplates';
    const [pageFilters, setPageFilters] = useState([]);

    useEffect(() => {
        CheckService.getTemplateOptions()
            .then(optionsResponse => {
                setTemplateOptions(optionsResponse);
            });
    }, []);

    useEffect(() => {
        if (templateOptions) {
            updateFilters(appliedFilters);
        }
    }, [templateOptions]);

    const updateFilters = (newAppliedFilters) => {
        if (!templateOptions) {
            return newAppliedFilters;
        }

        const categories = templateOptions.templateCategories;
        const categoryFilterKey = "category.categoryname";
        const subCategoryFilterKey = "subcategory.subcategoryname";

        const newFilters = [
            {
                name: "category.categoryname",
                displayName: "Category",
                items: categories.map((category) => ({ id: category.name, name: category.name }))
            },
        ]

        const selectedCategories = newAppliedFilters?.filter(x => x.name === categoryFilterKey)?.map(x => x.value);

        const subCategoryFilters = categories
            .filter(x => !selectedCategories?.length || selectedCategories.includes(x.name))
            .flatMap(x => x.subCategories.map(s => ({ id: s.name, name: s.name })));

        newFilters.push({
            name: subCategoryFilterKey,
            displayName: "Subcategory",
            items: subCategoryFilters
        });

        const applicableAppliedFilters = newAppliedFilters.filter(x => x.name !== subCategoryFilterKey
            || subCategoryFilters.some(f => f.id === x.value));

        if (!isGlobal) {
            newFilters.push({
                name: "templatetype.templatetypename",
                displayName: "Type",
                items: templateOptions.templateTypes.map(i => ({ id: i.name, name: i.name }))
            });
        }

        newFilters.push({
            name: "templatestatus.templatestatusname",
            displayName: "Status",
            items: templateOptions.templateStatuses.map(i => ({ id: i.name, name: i.name }))
        });

        setPageFilters(newFilters);
        setFilterSetupComplete(true);

        return applicableAppliedFilters;
    }

    useEffect(() => {
        setPageNumber(1);
        setNoDataMessage("No results found, please try a different search term.");
        setLoadingData(true);
    }, [debouncedSearchTerm, props.useGlobalConfig]);

    useEffect(() => {
        fetchTableData(false);
    }, [debouncedSearchTerm, pageSize, pageNumber, tableSortBy, tableSortDir, appliedFilters, initalSetupComplete]);

    useEffect(() => {
        fetchTableData(true);
    }, [props.useGlobalConfig]);

    useEffect(function () {
        setInitialSetupComplete(filterSetupComplete);
    }, [filterSetupComplete]);

    const canArchiveTemplate = (template) => {
        if (template.relatedEventType) {
            return {
                canArchive: false,
                reason: "This template is linked to an event type and cannot be archived."
            };
        }
        if (template.templateStatus.name === TemplateStatuses.ARCHIVED) {
            return {
                canArchive: false,
                reason: "This template is already archived."
            };
        }
        if (template.templateType.key === TemplateTypes.GLOBAL) {
            if (!(canEditGlobal && userIsSuperInternal)) {
                return {
                    canArchive: false,
                    reason: "You do not have permission to archive this global template."
                };
            }

        }
        if (template.templateType.key !== TemplateTypes.CUSTOM && !userIsSuperInternal) {
            return {
                canArchive: false,
                reason: "You do not have permission to archive this template."
            };
        }
        return {
            canArchive: true,
            reason: ""
        };
    };

    function fetchTableData(forceRefresh) {

        if (canAccessPage && initalSetupComplete) {

            if (currentPageNumber.current === pageNumber &&
                currentPageSize.current === pageSize &&
                currentTableSortBy.current === tableSortBy &&
                currentTableSortDir.current === tableSortDir &&
                currentAppliedFilters.current === appliedFilters.length &&
                currentSearchTerm.current === debouncedSearchTerm &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentTableSortBy.current = tableSortBy;
            currentTableSortDir.current = tableSortDir;
            currentAppliedFilters.current = appliedFilters.length;
            currentSearchTerm.current = debouncedSearchTerm;

            setLoadingData(true);

            let mappedFilters = appliedFilters.map(function (f) {
                return {
                    key: f.name,
                    ...f
                }
            });

            const searchRequest = {
                searchTerm: debouncedSearchTerm,
                requestCount: pageSize,
                pageNumber: pageNumber,
                sortField: tableSortBy.sortName,
                sortDirection: tableSortDir,
                filters: mappedFilters
            };

            CheckService.getAllTemplates(searchRequest, useGlobalEndpoint).then(r => {
                let data = [];

                if (r?.data != null) {
                    data = r.data.map((d) => {
                        const archiveInfo = canArchiveTemplate(d);
                        return [
                            <Link key={`link-${d.templateId}`} to={`${baseRoute}/${d.templateId}`}><StringToEllipsesString length={30} showTooltip={true}>{d.templateName}</StringToEllipsesString></Link>,
                            d.createDate ? <DateToLocal key={`createdate-${d.templateId}`} format='DD/MM/YYYY'>{d.createDate}</DateToLocal> : null,
                            d.publishedDate ? <DateToLocal key={`publishdate-${d.templateId}`} format='DD/MM/YYYY'>{d.publishedDate}</DateToLocal> : null,
                            d.archivedDate ? <DateToLocal key={`archivedate-${d.templateId}`} format='DD/MM/YYYY'>{d.archivedDate}</DateToLocal> : null,
                            d.category.name,
                            d.subCategory.name,
                            d.templateType.name,
                            d.templateStatus.name,
                            d.versionNumber,
                            d.numberOfChecksForCurrentVersion,
                            d.totalNumberOfChecksForAllVersions,
                            <LinkButton key={`clone-${d.templateId}`} minimal={true} large={false} intent="primary" onClick={() => onCloneClick(d)} icon="duplicate"></LinkButton>,
                            <Tooltip key={`archive-${d.templateId}`} content={archiveInfo.canArchive ? undefined : archiveInfo.reason}>
                                <LinkButton minimal={true} large={false} onClick={() => onArchiveClick(d)} intent="delete" disabled={!archiveInfo.canArchive} icon="archive" />
                            </Tooltip>
                        ]
                    })
                }

                setTableRows(data);
                setTotalRecords(r.totalCount);
                setLoadingData(false);
                setTimeout(function () {
                    if (inputRef.current != null) {
                        inputRef.current.focus();
                    }
                });
                dispatch(SetRouting(null, null, null));

            }, function () {
                NotificationToaster.show(Intent.DANGER, `Could not retrieve ${isGlobal ? 'standard ' : ''}templates, please try again`);
            });
        }
    }

    function onFilterChange(listingFilters) {
        setFilterSetupComplete(true);
        setPageNumber(1);
        setNoDataMessage("No results found, please try a different filter.");

        const applicableFilters = updateFilters(listingFilters);

        if (applicableFilters.length !== listingFilters?.length) {
            currentAppliedFilters.current = 0; // Force the page to refresh
            dispatch(SetFiltering(filterName, applicableFilters));
        }

        setAppliedFilters(applicableFilters);

        if (listingFilters.length > appliedFilters.length) {
            setShowFilters(true);
        }
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }
    function onSearchTermChange(event) {
        setSearchTerm(event.target.value);
    }

    const onArchiveClick = (selectTemplate) => {
        setShowArchiveTemplateConfirm(true);
        setSelectedTemplate(selectTemplate);
    }

    const onCloneClick = (selectTemplate) => {
        setShowCloneTemplateConfirm(true);
        setSelectedTemplate(selectTemplate);
    }

    const archiveTemplate = () => {

        if (!selectedTemplate) {
            NotificationToaster.show(Intent.DANGER, "No template ID selected for archiving.");
            return;
        }

        setShowArchiveTemplateConfirm(false);
        setLoadingData(true);
        setSaving(true);

        CheckService.setTemplateStatus(selectedTemplate.templateId, { status: TemplateStatuses.ARCHIVED, useGlobalEndpoint })
            .then(() => {
                setSaving(false);
                NotificationToaster.show(
                    Intent.SUCCESS,
                    "Template archived successfully"
                );
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to archive template");
            })
            .finally(() => {
                setSaving(false);
                setSelectedTemplate(null);
                fetchTableData(true);
            });
    }

    const cloneTemplate = () => {
        if (!selectedTemplate) {
            NotificationToaster.show(Intent.DANGER, "No template selected for cloning.");
            return;
        }

        if (!templateName || templateName === "") {
            NotificationToaster.show(Intent.DANGER, "New Template name cannot be empty.");
            return;
        }

        setShowCloneTemplateConfirm(false);
        setLoadingData(true);
        setSaving(true);

        CheckService.cloneTemplate(selectedTemplate.templateId, { TemplateName: templateName }, useGlobalEndpoint)
            .then((id) => {
                setSaving(false);
                NotificationToaster.show(
                    Intent.SUCCESS,
                    "Template duplicated successfully"
                );

                navigate(`${baseRoute}/${id}`);
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, error.response.data);
            })
            .finally(() => {
                setSaving(false);
                setSelectedTemplate(null);
                setTemplateName("");
                fetchTableData(true);
            });
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (

                <div className="row">

                    <Breadcrumbs items={props.breadcrumbs} />

                    <h1>{title}</h1>

                    <div className="button-row spacer-bottom">
                        <LinkButton
                            href={`${baseRoute}/new`}
                            text={isGlobal ? "New Standard Template" : "New Template"}
                            intent="primary"
                            large
                            id="new-template-button"
                            disabled={loadingData || saving}
                        />
                    </div>

                    <ShowHide evaluator={tableRows.length === 0 && !loadingData && debouncedSearchTerm === "" && pageFilters.length === 0}
                        hide={(
                            <Fragment>
                                <div className="spacer-bottom">
                                    <div className="filter-controls">
                                        <div className="inline-items">
                                            <div className="inline-item fill">
                                                <div className="form-field-full">
                                                    <FormTextInput inputRef={inputRef} placeholder={`${isGlobal ? 'Standard ' : ''}Template Search`} onChange={onSearchTermChange} value={searchTerm} large disabled={loadingData} icon="search" id="template-search-field" />
                                                </div>
                                            </div>
                                            <div className="inline-item">
                                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                            </div>
                                            
                                        </div>  
                                        <Filter filterName={filterName} visible={showFilters} numOfColumns={5} filters={pageFilters} onUpdate={onFilterChange} onClickOutside={onToggleFilter} />   
                                    </div>                               
                                </div>
                                
                            </Fragment>
                        )}
                    />

                    <ListingTable
                        id="listing-table-templates"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        onPagingChange={onPagingChange}
                        loadingData={loadingData}
                        noDataMessage={noDataMessage}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                    />

                    <AlertConfirm
                        title="Duplicate Template"
                        isOpen={showCloneTemplateConfirm}
                        onConfirm={cloneTemplate}
                        onCancel={() => setShowCloneTemplateConfirm(false)}
                        confirmButtonIntent="primary"
                        confirmButtonText="Duplicate Template">

                        <NotificationInline
                            allowClose={false}
                            show={selectedTemplate?.templateType.key === 'Global' && selectedTemplate?.subCategory.key === "WalkaroundCheck"}
                            text="You are about to amend a template that includes items that DVSA recommend are checked as part of a vehicle walk round check. Doing so may result in the vehicle being operated in an unroadworthy condition. Do you wish to continue?"
                            intent="warning"
                        ></NotificationInline>

                        <h4>New Template Name:</h4>
                        <FormTextInput inputRef={inputRef} placeholder="Enter template name" onChange={(e) => setTemplateName(e.target.value)} value={templateName} wide large disabled={loadingData} id="template-clone-field" />
                    </AlertConfirm>

                    <AlertConfirm
                        title="Archive Template"
                        isOpen={showArchiveTemplateConfirm}
                        onConfirm={archiveTemplate}
                        onCancel={() => setShowArchiveTemplateConfirm(false)}
                        confirmButtonIntent="primary"
                        confirmButtonText="Archive Template">
                        <p>Are you sure you want to archive the template?</p>
                    </AlertConfirm>
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}